<script setup>
import { ref } from 'vue';
import { onMounted } from 'vue';
import SlideGenerationSidebar from './Sidebar/SlideGenerationSidebar.vue';
import SnippetsSidebar from './Sidebar/SnippetsSidebar.vue';
import ImagesSidebar from './Sidebar/ImagesSidebar.vue';
import ChatSidebar from './Sidebar/ChatSidebar.vue';
import Spinner from './Spinner.vue';
import { safeParsePostMessage } from '@/utils/postMessageHelpers';
import { captureException } from '@sentry/vue';

const COLLABORA_ONLINE_URL = import.meta.env.VITE_COLLABORA_ONLINE_URL;

const props = defineProps(['presentationId'])
const emit = defineEmits(['image-clicked'])

var lastOpenedDeck = null;
const isSidebarOpen = ref(false);

const slideGenerationSidebar = ref(null);
const snippetsSidebar = ref(null);
const imagesSidebar = ref(null);
const chatSidebar = ref(null);

const sidebarIsTransitioning = ref(false);

const slideGenerationStatus = ref("pending");  // pending - loading - finished - failed

const sidebarsMap = {
  "options-slide-generation-sidebar": slideGenerationSidebar,
  "options-snippets-sidebar": snippetsSidebar,
  "options-images-sidebar": imagesSidebar,
  "options-chat-sidebar": chatSidebar,
};

onMounted(() => {
  const onMessage = async (e) => {
    const msg = safeParsePostMessage(e.data);
    if (msg.MessageId === "Clicked_Button") {
      if (msg.Values.Id === "options-navigator"
          || msg.Values.Id === "options-master-slides-panel"
          || msg.Values.Id === "options-custom-animation"
          || msg.Values.Id === "options-slide-change-window"
          || msg.Values.Id === "options-modify-page") {
        const sidebarBody = document.getElementById("sidebarBody");
        const sidebarIsOpen = sidebarBody.classList.contains("is-sidebar-open");

        if (sidebarIsOpen) {
          sidebarBody.classList.remove("is-sidebar-open");
        }
      } else if (msg.Values.Id === "options-slide-generation-sidebar"
                 || msg.Values.Id === "options-snippets-sidebar"
                 || msg.Values.Id === "options-images-sidebar"
                 || msg.Values.Id === "options-chat-sidebar") {
        toggleSidebar(msg.Values.Id)
      }
    }
  };
  window.addEventListener('message', onMessage, false);
});

function toggleSidebar(id, event) {
  if (!sidebarIsTransitioning.value) {
    sidebarIsTransitioning.value = true;
    const sidebarBody = document.getElementById("sidebar-body");
    const clickedBtn = event?.target;

    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
      "MessageId": "Sidebar_State",
      "SendTime": Date.now(),
      "Values": {}
    }), COLLABORA_ONLINE_URL);

    function onSidebarStateResp(e) {
      var msg = safeParsePostMessage(e.data);
      if (msg.MessageId === "Sidebar_State_Resp") {
        if (msg.Values.Deck) {
          lastOpenedDeck = msg.Values.Deck;
        }
        if (msg.Values.Visible === false && !isSidebarOpen.value) {
          isSidebarOpen.value = true;
          for (const [_, sidebarComponent] of Object.entries(sidebarsMap)) {
            sidebarComponent.value.close();
          }
          document.querySelectorAll("#sidebar-buttons div a").forEach((element) => {
            element.classList.remove("selected");
          });
          sidebarsMap[id].value.open();
          setTimeout(function () {
            sidebarIsTransitioning.value = false;
          }, 300);
          clickedBtn?.classList.add("selected");
          setTimeout(function () {
            document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
              "MessageId": "Send_UNO_Command",
              "SendTime": Date.now(),
              "Values": {
                "Command": ".uno:SidebarDeck",
                "Args": {}
              }
            }), COLLABORA_ONLINE_URL);
          }, 300);
        } else if (msg.Values.Visible === true && isSidebarOpen.value) {
          if (sidebarsMap[id].value.isOpened()) {
            document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
              "MessageId": "Send_UNO_Command",
              "SendTime": Date.now(),
              "Values": {
                "Command": ".uno:Sidebar",
                "Args": {}
              }
            }), COLLABORA_ONLINE_URL);
            sidebarsMap[id].value.close();
            isSidebarOpen.value = false;
            setTimeout(function () {
              sidebarIsTransitioning.value = false;
            }, 300);
          } else {
            for (const [_, sidebarComponent] of Object.entries(sidebarsMap)) {
              sidebarComponent.value.close();
            }
            sidebarsMap[id].value.open();
            setTimeout(function () {
              sidebarIsTransitioning.value = false;
            }, 300);
          }
        } else if (msg.Values.Visible === true && !isSidebarOpen.value) {
          for (const [_, sidebarComponent] of Object.entries(sidebarsMap)) {
            sidebarComponent.value.close();
          }
          sidebarsMap[id].value.open();
          setTimeout(function () {
            sidebarIsTransitioning.value = false;
          }, 300);
          isSidebarOpen.value = true;
        } else if (msg.Values.Visible === false && isSidebarOpen.value) {
          captureException(Error('Integration sidebar open while editor sidebar closed.'), { level: "error" });
        }
        window.removeEventListener("message", onSidebarStateResp, false);
      }
    }
    window.addEventListener("message", onSidebarStateResp, false);
  }
}

function imageClicked(imageSrc) {
  emit('image-clicked', imageSrc);
}

function updateSlideGenerationLoader(value) {
  slideGenerationStatus.value = value;
}

defineExpose({
  toggleSidebar,
});
</script>


<template>
  <div id="sidebar-app-2" :class="{ 'is-visible': isSidebarOpen }">
    <div id="sidebar-buttons">
      <div class="py-1" style="border-radius:2rem 0rem 0rem 2rem;box-shadow: 0 4px 15px rgba(138, 43, 226, 0.3);">
        <div v-if="slideGenerationStatus === 'finished' && !slideGenerationSidebar.isOpened()"
             class="tooltip has-text-dark">
          <div class="tooltiptext is-flex is-flex-direction-column m-2">
            <span>Slides Generated!</span>
            <div class="is-flex">
              <button class="button m-2" @click="slideGenerationSidebar.dismiss()">Dismiss</button>
              <button class="button m-2"
                      style="background-image: linear-gradient(45deg, #E100FF 0%, #7F00FF 100%); color: white; border: none;"
                      @click="slideGenerationSidebar.apply()">Apply</button>
            </div>
          </div>
        </div>
        <div v-else-if="slideGenerationStatus === 'failed' && !slideGenerationSidebar.isOpened()"
             class="tooltip has-text-dark">
          <div class="tooltiptext is-flex is-flex-direction-column m-2">
            <span class="px-2">Failed to generate slides.</span>
            <div>
              <button class="button m-2" @click="slideGenerationSidebar.dismiss()">Dismiss</button>
            </div>
          </div>
        </div>
        <a class="is-flex is-flex-direction-column is-flex-wrap-nowrap is-align-items-center p-2 m-1"
           :class="{ selected: slideGenerationSidebar?.isOpened() }"
           @click="toggleSidebar('options-slide-generation-sidebar', $event)">
          <img v-if="slideGenerationStatus !== 'loading' || slideGenerationSidebar.isOpened()"
               src="@/assets/images/plus-rectangle.svg" style="width: 2.4rem">
          <Spinner v-else-if="slideGenerationStatus === 'loading'" />
          <span class="is-size-7 has-text-weight-bold" style="color: #666666;">Generate</span>
        </a>
        <a class="is-flex is-flex-direction-column is-flex-wrap-nowrap is-align-items-center p-2 m-1"
           :class="{ selected: snippetsSidebar?.isOpened() }"
           @click="toggleSidebar('options-snippets-sidebar', $event)">
          <img src="@/assets/document.svg" style="width: 2.4rem;">
          <span class="is-size-7 has-text-weight-bold" style="color: #666666;">Provenance</span>
        </a>
        <a class="is-flex is-flex-direction-column is-flex-wrap-nowrap is-align-items-center m-1 p-2"
           :class="{ selected: chatSidebar?.isOpened() }"
           @click="toggleSidebar('options-chat-sidebar', $event)">
          <img src="@/assets/chat.svg" style="width: 2.4rem;">
          <span class="is-size-7 has-text-weight-bold" style="color: #666666;">Edit content</span>
        </a>
        <a class="is-flex is-flex-direction-column is-flex-wrap-nowrap is-align-items-center p-2 m-1"
           :class="{ selected: imagesSidebar?.isOpened() }"
           @click="toggleSidebar('options-images-sidebar', $event)">
          <img src="@/assets/gallery.svg" style="width: 2.4rem;">
          <span class="is-size-7 has-text-weight-bold" style="color: #666666;">AI Images</span>
        </a>
      </div>
    </div>
    <div id="sidebar-body">
      <SlideGenerationSidebar
        ref="slideGenerationSidebar"
        :isSlideGenerationLoading="isSlideGenerationLoading"
        :presentationId="presentationId"
        @onSidebarClosed="toggleSidebar('options-slide-generation-sidebar', null)"
        @onSlideGenerationUpdate="updateSlideGenerationLoader" />
      <SnippetsSidebar
        ref="snippetsSidebar"
        :presentationId="presentationId"
        :isSidebarOpen="isSidebarOpen"
        @onSidebarClosed="toggleSidebar('options-snippets-sidebar', null)"
        @onSidebarOpened="toggleSidebar('options-snippets-sidebar', $event)"
        @image-clicked="imageClicked" />
      <ImagesSidebar ref="imagesSidebar" />
      <ChatSidebar ref="chatSidebar" />
    </div>
  </div>
</template>

<style>
 body {
   overflow-x: hidden;
   /* Prevent horizontal scrolling during animations */
 }

 #sidebar-body {
   position: relative;
   background-color: white;
   width: 21rem;
 }

 #sidebar-app-2 {
   display: flex;
   flex-direction: row;
   /* width: 6rem; */
   /* background-color: white; */
   top: 7.5rem;
   bottom: 33px;
   right: 0rem;
   position: fixed;
   transition: transform 0.3s ease-in-out;
   transform: translateX(336px);
 }

 #sidebar-app-2.is-visible {
   transform: translateX(0%);
 }

 .sidebar {
   --var-item-side-width: 12rem;
   --var-item-slider-width: 30rem;
   --var-item-padding: 1rem;
   --var-item-gap: 1rem;
   bottom: 1rem;
   overflow: hidden;
   position: fixed;
   right: 1rem;
   z-index: 100;
 }

 .sidebar-body {
   background: var(--bulma-scheme-main);
   box-shadow: var(--bulma-shadow);
   border-radius: .0rem;
   transform-origin: bottom right;
   transition-duration: .3s;
   transition-property: opacity, transform;
   transform: scale(.98) translate(2px, 2px);
   opacity: 0;
   pointer-events: none;
   width: 20rem;
   top: 7.5rem;
   position: fixed;
   bottom: 2.6rem;
   right: 0rem;
   overflow-y: auto;
 }

 .is-sidebar-open {
   opacity: 1;
   pointer-events: auto;
   transform: none;
 }

 #sidebar-buttons div {
   background-color: white;
 }

 #sidebar-buttons div a {
   border: 3px solid transparent;
   border-left: 0;
   border-bottom: 0;
   border-top: 0;
 }

 #sidebar-buttons div a.selected {
   border-image-source: linear-gradient(to top, #7F00FF, #E100FF);
   border-image-slice: 1;
 }


 .gradient-purple-spinner {
   position: relative;
   width: 36px;
   height: 36px;
   border-radius: 50%;
   margin-bottom: 1px;
 }

 .gradient-purple-spinner::before,
 .gradient-purple-spinner::after {
   content: "";
   position: absolute;
   border-radius: inherit;
 }

 .gradient-purple-spinner::before {
   width: 100%;
   height: 100%;
   background-image: linear-gradient(0deg, #E100FF 0%, #7F00FF 100%);
   animation: load012323 .5s infinite linear;
 }

 .gradient-purple-spinner::after {
   width: 85%;
   height: 85%;
   background-color: #fff;
   top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);
 }

 @keyframes load012323 {
   to {
     transform: rotate(360deg);
   }
 }

 .tooltip {
   position: relative;
 }

 .tooltip .tooltiptext {
   width: max-content;
   text-align: center;
   border-radius: 1rem;
   padding: 0.5rem;
   position: absolute;
   z-index: 1;
   right: 110%;
   box-shadow: 0 0 10px rgba(138, 43, 226, 0.3);
 }

 .tooltip .tooltiptext::after {
   content: "";
   position: absolute;
   top: 50%;
   left: 100%;
   margin-top: -5px;
   border-width: 5px;
   border-style: solid;
   border-color: transparent transparent transparent white;
 }


 /* .tooltip {
    position: fixed;
    top: 7.5rem;
    right: 7rem;
    background: red;
    height: 10rem;
    } */
</style>
